import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import UIIcon from '@/components/UI/UIIcon'
import { useGSAP } from '@/composables/useGSAP'
import { useImageFormatContext } from '@/composables/useImageFormatContext'
import { getContentfulUrl } from '@/utils/contentful'

import { PartVariant } from './RunnerGoalEquation'

export type RunnerGoalEquationSearchedProps = {
  goal?: PartVariant
  collected?: boolean
}

export default function RunnerGoalEquationSearched({ goal, collected }: RunnerGoalEquationSearchedProps) {
  const { gsap } = useGSAP()

  const arrowRef = useRef<SVGSVGElement>(null)
  const arrowBodyRef = useRef<SVGPathElement>(null)
  const arrowHeadRef = useRef<SVGPathElement>(null)
  const arrowBodyAltRef = useRef<SVGPathElement>(null)
  const arrowHeadAltRef = useRef<SVGPathElement>(null)

  const betterFormat = useImageFormatContext()
  const [imageUrl, setImageUrl] = useState(null)
  useEffect(() => {
    if (!goal) return

    const url = getContentfulUrl(`/src/assets/runner/${goal.image}.png`, {
      w: window.innerWidth < 768 ? 108 : 188,
      fm: betterFormat,
    })
    setImageUrl(url)
  }, [goal, betterFormat])

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap
        .timeline({ delay: 0.5 })
        .to(arrowRef.current, { opacity: 1, duration: 0.5, ease: 'power2.out' }, 0)
        .fromTo(
          [arrowBodyAltRef.current, arrowBodyRef.current],
          { drawSVG: '0% 0%' },
          { drawSVG: '0% 100%', duration: 1, ease: 'power2.out', stagger: 0.15 },
          0
        )
        .fromTo(
          [arrowHeadAltRef.current, arrowHeadRef.current],
          { drawSVG: '50% 50%' },
          { drawSVG: '0% 100%', duration: 0.3, ease: 'power2.out', stagger: 0.15 },
          0.7
        )
    })

    return () => ctx.kill()
  }, [arrowBodyRef, arrowHeadRef, gsap])

  return (
    <span className='Equation__Searched relative w-54 h-54 xl:w-96 xl:h-96 flex items-center justify-center '>
      <UIIcon
        icon='background3'
        className={`absolute top-0 left-0 w-full h-full ${collected ? 'text-light-blue' : 'text-mid-blue'}`}
      />

      <span className={`block relative text-42 font-black text-cream rotate-6 ${collected ? 'opacity-0' : ''}`}>?</span>
      <div
        className={`absolute top-0 left-0 w-full h-full flex items-center justify-center transition-opacity duration-500 ease-out-quad ${collected ? '' : 'opacity-0'}`}
      >
        {goal &&
          (goal.text ? (
            <span className='block relative text-58 font-black text-cream'>{goal.text}</span>
          ) : (
            <div className={`relative block ${goal.small ? 'w-64 h-64' : 'w-full h-full'}`}>
              <img
                src={imageUrl}
                alt=' '
                className={`relative block w-full h-full object-contain transition-transform duration-700 ease-out-back scale-[60%]`}
              />
            </div>
          ))}
      </div>

      <svg
        ref={arrowRef}
        className='absolute top-[85%] -left-28 xl:-left-16 w-64 h-64 xl:w-80 xl:h-80 text-primary'
        viewBox='0 0 80 80'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          ref={arrowBodyAltRef}
          d='M20.1399 66.7258C66.9892 45.7391 41.195 19.7617 29.9175 28.0308C15.8207 38.3672 48.499 62.4067 64.5679 19.2146'
          stroke='currentColor'
          strokeWidth='2'
          className='opacity-30'
        />
        <path
          ref={arrowHeadAltRef}
          d='M66.5902 28.5113L64.8876 18.6602L55.43 22.7858'
          stroke='currentColor'
          strokeWidth='2'
          className='opacity-30'
        />
        <path
          ref={arrowBodyRef}
          d='M20.1399 66.7258C66.9892 45.7391 41.195 19.7617 29.9175 28.0308C15.8207 38.3672 48.499 62.4067 64.5679 19.2146'
          stroke='currentColor'
          strokeWidth='2'
        />
        <path
          ref={arrowHeadRef}
          d='M66.5902 28.5113L64.8876 18.6602L55.43 22.7858'
          stroke='currentColor'
          strokeWidth='2'
        />
      </svg>
    </span>
  )
}
